/**
 * @fileoverview Metadata de features para UI
 * @module utils/featureInfo
 */

import { FeatureName, FeatureInfo } from '../types/features';

/**
 * Información completa de cada feature para mostrar en UI
 */
export const FEATURE_INFO: Record<FeatureName, FeatureInfo> = {
  advancedTasks: {
    name: 'advancedTasks',
    icon: '📝',
    title: 'Tareas Avanzadas',
    description: 'Agrega horarios del día, fechas de finalización y más opciones a tus tareas',
    shortDescription: 'Configura horarios, fechas de fin y más opciones en tus tareas',
    category: 'tasks',
    includes: [
      'Horarios del día (mañana, tarde, noche)',
      'Fechas de finalización',
      'Control de recurrencia',
      'Generación con IA'
    ]
  },
  
  advancedActivities: {
    name: 'advancedActivities',
    icon: '⏱️',
    title: 'Actividades Avanzadas',
    description: 'Duración, tiempo de gracia, sistema de intercambios y políticas de repetición',
    shortDescription: 'Activa timers, intercambios y configuración avanzada en actividades',
    category: 'tasks',
    includes: [
      'Duración en minutos con timer visual',
      'Tiempo de gracia configurable',
      'Sistema de intercambios (actividad por actividad o puntos)',
      'Políticas de repetición (una vez, repetir, rebloquear)',
      'Penalizaciones por tardanza',
      'Generación con IA'
    ]
  },
  
  dashboardMode: {
    name: 'dashboardMode',
    icon: '🏠',
    title: 'Dashboard Avanzado',
    description: 'Activa funciones avanzadas en Home y Board: metas, desafíos, nota colaborativa y más',
    shortDescription: 'Incluye metas, desafíos, feed de actividad y herramientas colaborativas',
    category: 'organization',
    includes: [
      'Metas y desafíos personales',
      'Feed de actividad familiar',
      'Nota colaborativa en tiempo real',
      'Comunicaciones (contexto educativo)',
      'Dashboard unificado de progreso'
    ]
  },
  
  templateLibrary: {
    name: 'templateLibrary',
    icon: '📚',
    title: 'Rutinas',
    description: 'Explora más de 60 plantillas pre-hechas de tareas y rutinas para copiar en un click',
    shortDescription: 'Copia tareas y rutinas de nuestra biblioteca con 60+ templates',
    category: 'organization',
    includes: [
      'Más de 60 templates curados',
      'Filtros por categoría, edad y dificultad',
      'Búsqueda inteligente',
      'Importación con un click',
      'Sistema de favoritos'
    ]
  },
  
  calendar: {
    name: 'calendar',
    icon: '📅',
    title: 'Calendario',
    description: 'Vista de calendario completa con eventos, deadlines y planificación mensual',
    shortDescription: 'Organiza eventos y fechas importantes en vista de calendario',
    category: 'organization',
    includes: [
      'Vista mensual interactiva',
      'Eventos personalizados',
      'Deadlines de tareas',
      'Sincronización con Google Calendar',
      'Recordatorios automáticos'
    ]
  },
  
  groupGoals: {
    name: 'groupGoals',
    icon: '🎯',
    title: 'Metas Grupales',
    description: 'Objetivos colaborativos para toda la familia con seguimiento de racha y celebraciones',
    shortDescription: 'Crea objetivos familiares y alcanzalos juntos',
    category: 'ai',
    includes: [
      'Metas colaborativas',
      'Seguimiento de racha',
      'Celebraciones automáticas',
      'Estadísticas grupales',
      'Motivación compartida'
    ]
  },
  
  advancedRecipes: {
    name: 'advancedRecipes',
    icon: '👨‍🍳',
    title: 'Recetas Avanzadas',
    description: 'Importa recetas con IA desde URLs o fotos, usa templates y más opciones',
    shortDescription: 'Importa recetas con IA, usa tags y accede a biblioteca de templates',
    category: 'cooking',
    includes: [
      'Importar desde URL con IA',
      'Importar desde foto con IA',
      'Tags y categorías',
      'Tiempo de preparación',
      'Nivel de dificultad',
      'Biblioteca de templates de recetas'
    ]
  },
  
  mealPlanCalendar: {
    name: 'mealPlanCalendar',
    icon: '📆',
    title: 'Calendario de Comidas',
    description: 'Planifica el menú semanal de tu familia arrastrando recetas al calendario',
    shortDescription: 'Planifica el menú semanal usando tus recetas',
    category: 'cooking',
    includes: [
      'Planificación semanal visual',
      'Drag & drop de recetas',
      'Vista por comida (desayuno, almuerzo, cena)',
      'Repetir planes anteriores',
      'Compartir con la familia'
    ],
    dependency: 'advancedRecipes',
    dependencyMessage: 'Requiere tener 5+ recetas creadas'
  },
  
  shoppingList: {
    name: 'shoppingList',
    icon: '🛒',
    title: 'Lista de Compras',
    description: 'Genera lista automática de ingredientes desde tu plan de comidas semanal',
    shortDescription: 'Lista de compras automática desde tu plan de comidas',
    category: 'cooking',
    includes: [
      'Generación automática desde plan de comidas',
      'Agrupar por categoría de producto',
      'Marcar items como comprados',
      'Agregar items manuales',
      'Compartir lista con familia'
    ],
    dependency: 'mealPlanCalendar',
    dependencyMessage: 'Requiere Calendario de Comidas activo y al menos 1 semana planificada'
  },
  
  gritiAI: {
    name: 'gritiAI',
    icon: '💬',
    title: 'Griti IA - Asistente Virtual',
    description: 'Chatea con Griti, tu asistente virtual, para dudas sobre la plataforma y consejos',
    shortDescription: 'Tu asistente virtual personal para ayudarte con Logrit',
    category: 'ai',
    includes: [
      'Chat conversacional',
      'Respuestas personalizadas según tu familia',
      'Ayuda contextual',
      'Consejos de uso',
      'Disponible 24/7'
    ]
  }
};

/**
 * Obtiene información de una feature
 * @param t - Función de traducción opcional (i18next). Si se pasa, traduce title/description/includes.
 */
export function getFeatureInfo(featureName: FeatureName, t?: (key: string, opts?: any) => string): FeatureInfo {
  const info = FEATURE_INFO[featureName];
  if (t) {
    return {
      ...info,
      title: t(`personalization.features.${featureName}.title`, { defaultValue: info.title }),
      description: t(`personalization.features.${featureName}.description`, { defaultValue: info.description }),
      shortDescription: t(`personalization.features.${featureName}.shortDescription`, { defaultValue: info.shortDescription }),
      includes: info.includes.map((item, i) =>
        t(`personalization.features.${featureName}.includes${i}`, { defaultValue: item })
      ),
    };
  }
  return info;
}

/**
 * Obtiene todas las features de una categoría
 */
export function getFeaturesByCategory(category: FeatureInfo['category']): FeatureInfo[] {
  return Object.values(FEATURE_INFO).filter(f => f.category === category);
}

/**
 * Retorna ruta de navegación para una feature (si aplica)
 */
export function getFeatureRoute(featureName: FeatureName): string | null {
  const routes: Partial<Record<FeatureName, string>> = {
    templateLibrary: '/templates',
    calendar: '/calendar',
    mealPlanCalendar: '/meal-plan',
    shoppingList: '/shopping-list'
  };
  
  return routes[featureName] || null;
}

/**
 * Categorías en orden de prioridad para mostrar banners
 */
export const CATEGORY_PRIORITY: FeatureInfo['category'][] = [
  'tasks',
  'organization',
  'cooking',
  'ai'
];

/**
 * Features en orden de prioridad para banners de invitación
 */
export const FEATURE_PRIORITY: FeatureName[] = [
  'templateLibrary',    // Más útil para usuarios que ya crean tareas
  'advancedActivities', // Funcionalidad clave
  'advancedTasks',      // Funcionalidad clave
  'mealPlanCalendar',   // Si tiene recetas
  'calendar',           // Organización general
  'advancedRecipes',    // Si usa cocina
  'gritiAI',            // Ayuda general
  'groupGoals',         // Gamificación
  'shoppingList'        // Último porque depende de otros
];
